import { client } from "../utils/axios";
import { CloudinaryImage, isCloudinaryImage } from "./media";
import { AdminComment, formatAdminComment } from "./comment";
import { formatOrder, Order } from "./order";
import {
  buildQueryParams as pageBuildQueryParams,
  isPage,
  Page,
  parseQueryParams as pageParseQueryParams,
  QueryParams as PageQueryParams,
} from "./page";
import { formatSubscription, Subscription } from "./subscriptions";
import { isObject, typeFormatFromArray } from "./utils";

export type UserMetadata = {
  creationTime: Date;
  lastLogInTime: Date;
};

export function isUserMetadata(x: unknown): x is UserMetadata {
  return (
    typeof x === "object" &&
    (x as UserMetadata).creationTime instanceof Date &&
    (x as UserMetadata).lastLogInTime instanceof Date
  );
}

export type User = {
  uid: string;
  email?: string;
  providers: string[];
  userMetadata: UserMetadata;
};

export type UserDetail = User & {
  avatar?: CloudinaryImage;
  nickname?: string;
  lastName?: string;
  firstName?: string;
  birthYear?: number;
  prefecture?: string;
  gender?: string;
};

export type UserListItem = User & {
  avatar?: CloudinaryImage;
  nickname?: string;
  lastName?: string;
  firstName?: string;
  birthYear?: number;
  prefecture?: string;
  gender?: string;
  hasSubscription: boolean;
  hasOneTimePurchase: boolean;
};

export function isUser(x: unknown): x is User {
  return (
    typeof x === "object" &&
    typeof (x as User).uid === "string" &&
    (typeof (x as User).email === "undefined" ||
      typeof (x as User).email === "string") &&
    Array.isArray((x as User).providers) &&
    (x as User).providers.every((p) => typeof p === "string") &&
    isUserMetadata((x as User).userMetadata)
  );
}

export function isUserListItem(x: unknown): x is UserListItem {
  return (
    isUser(x) &&
    ((x as UserListItem).avatar === undefined ||
      isCloudinaryImage((x as UserListItem).avatar)) &&
    ((x as UserListItem).nickname === undefined ||
      typeof (x as UserListItem).nickname === "string") &&
    ((x as UserListItem).lastName === undefined ||
      typeof (x as UserListItem).lastName === "string") &&
    ((x as UserListItem).firstName === undefined ||
      typeof (x as UserListItem).firstName === "string") &&
    ((x as UserListItem).birthYear === undefined ||
      typeof (x as UserListItem).birthYear === "number") &&
    ((x as UserListItem).prefecture === undefined ||
      typeof (x as UserListItem).prefecture === "string") &&
    ((x as UserListItem).gender === undefined ||
      typeof (x as UserListItem).gender === "string") &&
    typeof (x as UserListItem).hasSubscription === "boolean" &&
    typeof (x as UserListItem).hasOneTimePurchase === "boolean"
  );
}

export function isUserDetail(x: unknown): x is UserDetail {
  return (
    isUser(x) &&
    ((x as UserDetail).avatar === undefined ||
      isCloudinaryImage((x as UserDetail).avatar)) &&
    ((x as UserDetail).nickname === undefined ||
      typeof (x as UserDetail).nickname === "string") &&
    ((x as UserDetail).lastName === undefined ||
      typeof (x as UserDetail).lastName === "string") &&
    ((x as UserDetail).firstName === undefined ||
      typeof (x as UserDetail).firstName === "string") &&
    ((x as UserDetail).birthYear === undefined ||
      typeof (x as UserDetail).birthYear === "number") &&
    ((x as UserDetail).prefecture === undefined ||
      typeof (x as UserDetail).prefecture === "string") &&
    ((x as UserDetail).gender === undefined ||
      typeof (x as UserDetail).gender === "string")
  );
}

function formatUserMetadata(x: unknown): UserMetadata | null {
  if (!isObject(x)) {
    return null;
  }
  const { creationTime, lastLogInTime } = x as {
    creationTime?: unknown;
    lastLogInTime?: unknown;
  };
  if (typeof creationTime !== "string") {
    return null;
  }
  if (typeof lastLogInTime !== "string") {
    return null;
  }
  return {
    creationTime: new Date(creationTime),
    lastLogInTime: new Date(lastLogInTime),
  };
}

function formatUserListItem(x: unknown): UserListItem | null {
  if (!isObject(x)) {
    return null;
  }
  const userMetadata = formatUserMetadata((x as User).userMetadata);
  if (!userMetadata) {
    return null;
  }
  const res = {
    ...(x as Record<string, unknown>),
    userMetadata,
  };
  if (!isUserListItem(res)) {
    return null;
  }
  return res;
}

function formatUserDetail(x: unknown): UserDetail | null {
  if (!isObject(x)) {
    return null;
  }
  const userMetadata = formatUserMetadata((x as User).userMetadata);
  if (!userMetadata) {
    return null;
  }
  const res = {
    ...(x as Record<string, unknown>),
    userMetadata,
  };
  if (!isUserDetail(res)) {
    return null;
  }
  return res;
}

export async function getUser(uid: string): Promise<UserDetail> {
  const c = await client();
  const res = await c.get(`/users/${uid}`);
  const user = formatUserDetail(res.data);
  if (user) {
    return user;
  }
  throw Error("failed to get user");
}

export type QueryParams = {
  uid?: string;
  email?: string;
  name?: string;
  nickname?: string;
  hasSubscription?: boolean;
  hasOneTimePurchase?: boolean;
} & PageQueryParams;

const UID = "uid";
const EMAIL = "email";
const NAME = "name";
const NICKNAME = "nickname";
const HAS_SUBSCRIPTION = "hasSubscription";
const HAS_ONE_TIME_PURCHASE = "hasOneTimePurchase";

export function parseQueryParams(search: string): QueryParams {
  const params = new URLSearchParams(search);
  const result: QueryParams = {};

  const uid = params.get(UID);
  if (uid) result[UID] = uid;

  const email = params.get(EMAIL);
  if (email) result[EMAIL] = email;

  const name = params.get(NAME);
  if (name) result[NAME] = name;

  const nickname = params.get(NICKNAME);
  if (nickname) result[NICKNAME] = nickname;

  const hasSubscription = params.get(HAS_SUBSCRIPTION);
  if (hasSubscription === "true") result[HAS_SUBSCRIPTION] = true;
  if (hasSubscription === "false") result[HAS_SUBSCRIPTION] = false;

  const hasOneTimePurchase = params.get(HAS_ONE_TIME_PURCHASE);
  if (hasOneTimePurchase === "true")
    result[HAS_ONE_TIME_PURCHASE] = true;
  if (hasOneTimePurchase === "false")
    result[HAS_ONE_TIME_PURCHASE] = false;

  const page = pageParseQueryParams(search);

  return { ...result, ...page };
}

export function buildQueryParams(params: QueryParams): URLSearchParams {
  const init: string[][] = [];

  const p = new URLSearchParams();
  const {
    uid,
    email,
    name,
    nickname,
    hasSubscription,
    hasOneTimePurchase,
  } = params;
  if (uid) {
    p.set(UID, uid);
  }
  if (email) {
    p.set(EMAIL, email);
  }
  if (name) {
    p.set(NAME, name);
  }
  if (nickname) {
    p.set(NICKNAME, nickname);
  }
  if (typeof hasSubscription === "boolean") {
    p.set(HAS_SUBSCRIPTION, hasSubscription.toString());
  }
  if (typeof hasOneTimePurchase === "boolean") {
    p.set(HAS_ONE_TIME_PURCHASE, hasOneTimePurchase.toString());
  }
  init.push(...Array.from(p));

  const pp = pageBuildQueryParams(params);
  init.push(...Array.from(pp));

  return new URLSearchParams(init);
}

export async function getUsers(
  params?: QueryParams
): Promise<{ users: UserListItem[]; page: Page; totalCount: number }> {
  const c = await client();
  const res = await c.get("/users", { params });
  const { users: resUsers, page: resPage, totalCount } = res.data;
  const users = typeFormatFromArray(resUsers, formatUserListItem);
  if (users && isPage(resPage)) {
    return { users, page: resPage, totalCount: totalCount ?? 0 };
  }
  throw Error("failed to get users");
}

export async function getUserSubscriptions(
  uid: string,
  page?: number
): Promise<{ subscriptions: Subscription[]; page: Page }> {
  const c = await client();
  const res = await c.get(`/users/${uid}/subscriptions`, { params: { page } });
  const { subscriptions: resSubscriptions, page: resPage } = res.data;
  const subscriptions = typeFormatFromArray(
    resSubscriptions,
    formatSubscription
  );
  if (subscriptions && isPage(resPage)) {
    return { subscriptions, page: resPage };
  }
  throw Error("failed to get user subscriptions");
}

export async function getUserOrders(
  uid: string,
  page?: number
): Promise<{ orders: Order[]; page: Page }> {
  const c = await client();
  const res = await c.get(`/users/${uid}/orders`, { params: { page } });
  const { orders: resOrders, page: resPage } = res.data;
  const orders = typeFormatFromArray(resOrders, formatOrder);
  if (orders && isPage(resPage)) {
    return { orders, page: resPage };
  }
  throw Error("failed to get user orders");
}

export async function getUserAdminComments(
  uid: string,
  page?: number
): Promise<{ comments: AdminComment[]; page: Page }> {
  const c = await client();
  const res = await c.get(`/users/${uid}/adminComments`, { params: { page } });
  const { comments: resComments, page: resPage } = res.data;
  const comments = typeFormatFromArray(resComments, formatAdminComment);
  if (comments && isPage(resPage)) {
    return { comments, page: resPage };
  }
  throw Error("failed to get user admin comments");
}

export async function postUserAdminComment(
  uid: string,
  comment: string
): Promise<AdminComment> {
  const c = await client();
  const res = await c.post(`/users/${uid}/adminComments`, { comment });
  const adminComment = formatAdminComment(res.data);
  if (adminComment) {
    return adminComment;
  }
  throw Error("failed to post user admin comment");
}
